// Construcción de los registros y eventos del contrato de integración con
// Vimos Digital (directorio público de tiendas).
//
// Contrato (fuente de verdad): docs/arquitectura/especificacion-integracion-vimos-digital.md
//
// Esta capa es PURA (sin I/O): centraliza el mapeo de los datos internos del
// SaaS (empresa + empresa_perfil + elegibilidad de plan) al shape exacto que
// usa Vimos:
//   - `data` del webhook  → SaasStoreRecord
//   - payload de evento  → { event_id, event_type, occurred_at, data }
//   - respuestas de los endpoints de reconciliación GET
//
// Regla: la elegibilidad (feature `directorio_vimos`) se resuelve por
// features del plan, nunca por nombre de plan. La visibilidad real en el
// directorio es: plan elegible AND consentimiento explícito del usuario
// (perfil.is_visible_in_directory) AND requisitos del directorio cumplidos
// (categoría, ciudad, logo y descripción — ver `validarRequisitosDirectorio`)
// AND estado disponible de la tienda (en_prueba/activa). Ningún proceso
// posterior (webhook, worker, export, reconciliación) puede publicar una
// tienda en un estado no disponible o con requisitos incompletos.

import { categoriaTiendaValida } from './categorias-tienda.js';

// Re-export del mapeo categoría -> categoría Vimos (definido en
// ./categorias-tienda.js). Las Edge Functions del contrato importan sus
// helpers desde vimos.js (módulo canónico de la integración).
export { categoriaTiendaParaVimos } from './categorias-tienda.js';

/**
 * Claves de feature que controlan la integración con el directorio.
 * Vive en plan_feature (semilla Básico/Medio/Pro = true). No se consulta por
 * nombre de plan — todo pasa por `tieneFeature`/planta de features.
 */
export const FEATURE_DIRECTORIO_VIMOS = 'directorio_vimos';

/** Tipos de evento permitidos por el contrato Vimos. */
export const EVENTOS_VIMOS = [
  'store.created',
  'store.updated',
  'store.published_toggle',
  'store.deleted',
];

/**
 * Estados de `empresa` en los que la tienda está disponible y puede
 * aparecer en el directorio. Mismo criterio de disponibilidad pública del
 * SaaS (puede_operar): activa o en periodo de prueba. Cualquier otro estado
 * (suspendida, vencida, prueba_terminada, dada_de_baja, borrador) NO debe
 * quedar visible en Vimos.
 */
export const ESTADOS_TIENDA_VISIBLE_DIRECTORIO = ['en_prueba', 'activa'];

/**
 * True si el estado de la tienda la mantiene disponible para el directorio.
 * @param {string|undefined} estado
 * @returns {boolean}
 */
export function estadoTiendaVisibleDirectorio(estado) {
  return ESTADOS_TIENDA_VISIBLE_DIRECTORIO.includes(estado);
}

/**
 * Requisitos obligatorios para que una tienda aparezca en el directorio.
 * Claves estables con las que `validarRequisitosDirectorio` reporta lo que
 * falta; son las mismas que usa el panel cliente para guiar al dueño.
 */
export const REQUISITOS_DIRECTORIO = ['categoria', 'ciudad', 'logo', 'descripcion'];

/**
 * Etiqueta legible de cada requisito (para mensajes de UX en el panel).
 */
export const REQUISITOS_DIRECTORIO_ETIQUETA = {
  categoria: 'categoría',
  ciudad: 'ciudad',
  logo: 'logo',
  descripcion: 'descripción',
};

/**
 * True si el valor es un string no vacío (ignorando espacios al inicio/fin).
 * @param {unknown} valor
 * @returns {boolean}
 */
function esTextoNoVacio(valor) {
  return typeof valor === 'string' && valor.trim().length > 0;
}

/**
 * Valida que un perfil cumpla los requisitos mínimos para el directorio:
 * categoría de tienda informada (catálogo), ciudad, logo y descripción.
 * Devuelve `faltantes` (solo las claves que faltan) y `valido` (true solo si
 * no falta ninguna). Un perfil inexistente (null) o vacío reporta los cuatro.
 *
 * La categoría usa su helper específico (`categoriaTiendaValida`); el resto
 * se considera cumplido con un texto no vacío. Esta función es PURA y
 * reutilizable: la consume el panel (UX + guardado), `determinarVisibilidadDirectorio`
 * y, por ende, webhook/worker/export/reconciliación a través de la regla.
 *
 * @param {{ categoria?: unknown, ciudad?: unknown, logo_url?: unknown, descripcion?: unknown }|null|undefined} perfil
 * @returns {{ valido: boolean, faltantes: string[] }}
 */
export function validarRequisitosDirectorio(perfil) {
  const faltantes = [];
  if (!categoriaTiendaValida(perfil?.categoria)) faltantes.push('categoria');
  if (!esTextoNoVacio(perfil?.ciudad)) faltantes.push('ciudad');
  if (!esTextoNoVacio(perfil?.logo_url)) faltantes.push('logo');
  if (!esTextoNoVacio(perfil?.descripcion)) faltantes.push('descripcion');
  return { valido: faltantes.length === 0, faltantes };
}

/**
 * Obtiene la colección de features de un plan sin importar la forma de origen.
 * Soporta los dos orígenes reales del sistema:
 *   - `plan.features`      (rutas de reconciliación / export: la RPC
 *                          `fn_vimos_listar_modificadas` agrega los features
 *                          bajo la clave `features`)
 *   - `plan.plan_feature`  (embed PostgREST `plan:plan_id(plan_feature(*))`
 *                          que usa `consultarEmpresaVimos` en el worker)
 * Esta es la ÚNICA fuente de normalización de features; los consumidores no
 * deben duplicar la derivación.
 *
 * @param {{ features?: Array<{clave: string, valor: unknown}>, plan_feature?: Array<{clave: string, valor: unknown}> }|null|undefined} plan
 * @returns {Array<{clave: string, valor: unknown}>}
 */
export function obtenerFeaturesPlan(plan) {
  return plan?.features ?? plan?.plan_feature ?? [];
}

/**
 * Determina si la tienda es elegible para el directorio según su plan.
 * @param {{ features?: Array<{clave: string, valor: unknown}>, plan_feature?: Array<{clave: string, valor: unknown}> }} planEfectivo
 * @returns {boolean}
 */
export function planEsElegibleDirectorio(planEfectivo) {
  const features = obtenerFeaturesPlan(planEfectivo);
  if (!features.length) return false;
  const feature = features.find((f) => f.clave === FEATURE_DIRECTORIO_VIMOS);
  return feature ? Boolean(feature.valor) : false;
}

/**
 * Visibilidad real en el directorio = plan elegible AND consentimiento del
 * dueño AND requisitos del directorio cumplidos (categoría, ciudad, logo y
 * descripción — ver `validarRequisitosDirectorio`) AND estado disponible de
 * la tienda (en_prueba/activa). Una tienda en cualquier otro estado, o con
 * requisitos incompletos, NUNCA es visible, aunque tenga plan y
 * consentimiento: la regla es idéntica en eventos, worker, export y
 * reconciliación.
 *
 * Un `estado` ausente/desconocido se trata como no visible (default seguro).
 *
 * @param {object} parametros
 * @param {{ features?: Array<{clave: string, valor: unknown}>, plan_feature?: Array<{clave: string, valor: unknown}> }} parametros.planEfectivo
 * @param {{ is_visible_in_directory?: boolean, categoria?: string, ciudad?: string, logo_url?: string, descripcion?: string }} [parametros.perfil]
 * @param {string|undefined} [parametros.estado]
 * @returns {boolean}
 */
export function determinarVisibilidadDirectorio({ planEfectivo, perfil, estado }) {
  if (!estadoTiendaVisibleDirectorio(estado)) return false;
  if (!planEsElegibleDirectorio(planEfectivo)) return false;
  if (!validarRequisitosDirectorio(perfil).valido) return false;
  return Boolean(perfil?.is_visible_in_directory);
}

/**
 * Genera un event_id único global (`evt_...`) usando crypto.
 * @returns {string}
 */
export function generarEventId() {
  const uuid = crypto.randomUUID ? crypto.randomUUID() : randomUuidFallback();
  return `evt_${uuid.replace(/-/g, '')}`;
}

function randomUuidFallback() {
  const hex = '0123456789abcdef';
  let s = '';
  for (let i = 0; i < 32; i += 1) {
    s += hex[Math.floor(Math.random() * 16)];
  }
  return s;
}

/**
 * Construye el registro `data` (SaasStoreRecord) para Vimos, en el shape
 * exacto del contrato (sección 6.3). Los campos opcionales sin valor se
 * envían como null (Vimos los guarda como null).
 *
 * @param {object} parametros
 * @param {string} parametros.store_id
 * @param {string} parametros.slug
 * @param {string} parametros.name
 * @param {string|null} [parametros.description]
 * @param {string|null} [parametros.logo_url]
 * @param {string|null} [parametros.cover_image_url]
 * @param {string} parametros.city
 * @param {string} parametros.category
 * @param {string} parametros.store_url
 * @param {boolean} parametros.is_visible_in_directory
 * @param {string} parametros.updated_at
 * @returns {{
 *   store_id: string, slug: string, name: string,
 *   description: string|null, logo_url: string|null, cover_image_url: string|null,
 *   city: string, category: string, store_url: string,
 *   is_visible_in_directory: boolean, updated_at: string
 * }}
 */
export function construirRegistroVimos(parametros) {
  const v = {
    store_id: parametros.store_id,
    slug: parametros.slug,
    name: parametros.name,
    description: parametros.description === undefined ? null : (parametros.description ?? null),
    logo_url: parametros.logo_url ?? null,
    cover_image_url: parametros.cover_image_url ?? null,
    city: parametros.city,
    category: parametros.category,
    store_url: parametros.store_url,
    is_visible_in_directory: Boolean(parametros.is_visible_in_directory),
    updated_at: parametros.updated_at,
  };
  // description es opcional; si la omiten o viene null, se envía null.
  if (parametros.description !== undefined && parametros.description !== null) {
    v.description = parametros.description;
  } else {
    v.description = null;
  }
  return v;
}

/**
 * Construye el payload de evento para webhooks (sección 3).
 *
 * @param {object} parametros
 * @param {string} parametros.event_id
 * @param {'store.created'|'store.updated'|'store.published_toggle'|'store.deleted'} parametros.event_type
 * @param {string} parametros.occurred_at - ISO 8601
 * @param {ReturnType<typeof construirRegistroVimos>} parametros.data
 * @returns {{
 *   event_id: string, event_type: string, occurred_at: string, data: object
 * }}
 */
export function construirPayloadEventoVimos({ event_id, event_type, occurred_at, data }) {
  return {
    event_id,
    event_type,
    occurred_at,
    data,
  };
}

/**
 * Convierte una fecha a string ISO 8601 UTC (formato que espera Vimos).
 * @param {string|number|Date} fecha
 * @returns {string}
 */
export function aIsoUtc(fecha) {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return new Date(0).toISOString();
  return d.toISOString();
}

/**
 * Ejecuta cronológico: devuelve true si `reciente > previo`. Usado para
 * preservar la monotonicidad de updated_at por tienda.
 * @param {string|number|Date} reciente
 * @param {string|number|Date|null|undefined} previo
 * @returns {boolean}
 */
export function esActualizacionMasNueva(reciente, previo) {
  if (!previo) return true;
  return new Date(reciente).getTime() > new Date(previo).getTime();
}
